import React, { LegacyRef } from 'react';
import type { NamedProps } from 'react-select';
import DefaultSelect from 'react-select';
import AsyncSelect from 'react-select/async';
import { GroupTypeBase, OptionTypeBase } from 'react-select/src/types';
import { SelectComponents } from 'react-select/src/components';
import clsx from 'clsx';

import { NoOptionsMessage } from './NoOptionsMessage';
import { Control } from './Control';
import { Placeholder } from './Placeholder';
import { ValueContainer } from './ValueContainer';
import { MultiValue } from './MultiValue';
import { SingleValue } from './SingleValue';
import { Menu } from './Menu';
import { Option } from './Option';
import { DropdownIndicator } from './DropdownIndicator';
import { ClearIndicator } from './ClearIndicator';

import { selectTheme, useStyles } from './styles';
import i18n from 'locales/i18n';
import StateManager from 'react-select';
import Select from 'react-select/base';

export const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator,
  ClearIndicator,
} as Partial<SelectComponents<OptionTypeBase, boolean, GroupTypeBase<OptionTypeBase>>>;

export type SelectOption = {
  value: string | number;
  label: string;
};

type FetchAllItemsFunction = (search: string) => Promise<Array<SelectOption>>;

type AsyncSelectProps = {
  isAsync?: boolean;
  fetchItems?: FetchAllItemsFunction;
};
type SelectType = typeof AsyncSelect & typeof DefaultSelect;

type NamedSelectProps = NamedProps<OptionTypeBase, boolean>;

export type SelectInputProps = NamedSelectProps &
  AsyncSelectProps & {
    name: string;
    label?: string;
    value?: SelectOption | SelectOption[];
    additionalLabelClass?: string;
    components?: Partial<SelectComponents<OptionTypeBase, boolean, GroupTypeBase<OptionTypeBase>>>;
  };

export const SelectInput = React.forwardRef(
  (
    {
      label,
      name,
      isAsync = false,
      fetchItems,
      additionalLabelClass,
      components: componentsProps,
      ...props
    }: SelectInputProps,
    ref: LegacyRef<
      StateManager<
        OptionTypeBase,
        boolean,
        GroupTypeBase<OptionTypeBase>,
        Select<OptionTypeBase, boolean, GroupTypeBase<OptionTypeBase>>
      >
    >
  ) => {
    const direction = i18n.dir();

    const { label: labelClass, ...classes } = useStyles({ direction });

    const Select = (isAsync ? AsyncSelect : DefaultSelect) as SelectType;

    return (
      <>
        {label && (
          <label
            htmlFor={name}
            className={clsx(labelClass, { [additionalLabelClass as string]: !!additionalLabelClass })}
          >
            {label}
          </label>
        )}
        <Select
          {...props}
          ref={ref}
          isAsync={isAsync}
          cacheOptions
          loadOptions={fetchItems}
          defaultOptions
          components={{
            ...components,
            ...componentsProps,
            IndicatorSeparator: () => null,
          }}
          theme={selectTheme}
          classes={classes}
        />
      </>
    );
  }
);
